import { useState } from 'react';
import { RecipeService } from '@/lib/recipeService';
import { toaster } from '@/lib/toaster';

interface UseRecipeImageUploadOptions {
  recipeId: string;
  onSuccess?: () => void;
}

/**
 * Hook for uploading a recipe image.
 */
export function useRecipeImageUpload({
  recipeId,
  onSuccess,
}: UseRecipeImageUploadOptions) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const uploadImage = async (file: File): Promise<void> => {
    setIsUploading(true);
    setError(null);
    try {
      await RecipeService.uploadImage(recipeId, file);
      onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e);
      const message = e.message;
      let toastMessage = 'Failed to update recipe image';
      if (message === 'Could not prepare upload') toastMessage = 'Could not prepare upload';
      else if (message === 'Upload failed') toastMessage = 'Upload failed';
      else if (message === 'Confirmation failed') toastMessage = 'Confirmation failed';
      toaster.create({ title: toastMessage, description: 'Please try again', type: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  return { uploadImage, isUploading, error };
}
